import { useState, useCallback } from 'react';
import { useAuth } from './useAuth';
import { useToast } from './use-toast';

interface AuthResilienceOptions {
  maxRetries?: number;
  retryDelay?: number;
  onAuthRequired?: () => void;
}

export const useAuthResilience = (options: AuthResilienceOptions = {}) => {
  const { maxRetries = 2, retryDelay = 1000, onAuthRequired } = options;
  const { user, session, isSessionValid, refreshSession, checkAuthStatus } = useAuth();
  const { toast } = useToast();
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Enhanced error detection for auth-related issues
  const isAuthError = (error: any): boolean => {
    if (!error) return false;
    
    const message = (error.message || error.msg || '').toString().toLowerCase();
    const code = (error.code || '').toString().toLowerCase();
    const status = (error.status || error.statusCode || error.httpStatus || '').toString();
    
    return (
      message.includes('authentication_required') ||
      message.includes('not authenticated') ||
      message.includes('user not authenticated') ||
      message.includes('unauthorized') ||
      message.includes('invalid token') ||
      message.includes('invalid jwt') ||
      message.includes('jwt expired') ||
      message.includes('session not found') ||
      code === '401' ||
      code === 'unauthorized' ||
      status === '401'
    );
  };

  // Handle authentication errors with user-friendly messages
  const handleAuthError = useCallback((error: any, context: string = '') => {
    console.error(`Auth error in ${context}:`, error);
    
    if (isAuthError(error)) {
      toast({
        title: "Authentication Required",
        description: "Your session has expired. Please refresh the page and sign in again.",
        variant: "destructive",
        duration: 8000,
      });
      
      if (onAuthRequired) {
        onAuthRequired();
      }
      
      return true; // Indicates this was an auth error
    }
    
    return false; // Not an auth error
  }, [toast, onAuthRequired]);

  // Wrapper for operations that require authentication with retry logic
  const withAuthRetry = useCallback(async <T>(
    operation: () => Promise<T>,
    context: string = 'operation'
  ): Promise<T> => {
    let lastError: any;
    
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        // Check auth status before attempting operation
        if (!user || !session) {
          throw new Error('User not authenticated');
        }

        // Proactively refresh if the token is near expiry
        try {
          const expiresAtMs =
            typeof (session as any)?.expires_at === 'number'
              ? (session as any).expires_at * 1000
              : ((session as any)?.expires_at ? Date.parse((session as any).expires_at as any) : null);
          const thresholdMs = Math.max(5000, retryDelay);
          if (expiresAtMs && expiresAtMs - Date.now() < thresholdMs) {
            console.log('Session near expiry, refreshing before operation...');
            setIsRefreshing(true);
            const { error: refreshError } = await refreshSession();
            if (refreshError) {
              console.error('Pre-operation refresh failed:', refreshError);
              throw new Error('authentication_required');
            }
            await new Promise((resolve) => setTimeout(resolve, 300));
          }
        } finally {
          setIsRefreshing(false);
        }
        
        // Check if session is still valid
        if (!isSessionValid) {
          console.log(`Session invalid on attempt ${attempt + 1}, checking status...`);
          const isValid = await checkAuthStatus();
          
          if (!isValid && attempt < maxRetries) {
            console.log('Attempting session refresh...');
            setIsRefreshing(true);
            
            const { error: refreshError } = await refreshSession();
            
            if (refreshError) {
              console.error('Session refresh failed:', refreshError);
              throw new Error('authentication_required');
            }
            
            // Wait a moment for the session to be updated
            await new Promise(resolve => setTimeout(resolve, 500));
            setIsRefreshing(false);
            
            // Continue to retry the operation
            continue;
          }
        }
        
        // Attempt the operation
        const result = await operation();
        return result;
        
      } catch (error) {
        lastError = error;
        console.error(`${context} failed on attempt ${attempt + 1}:`, error);
        
        // If it's an auth error and we haven't reached max retries, try to refresh
        if (isAuthError(error) && attempt < maxRetries) {
          console.log('Auth error detected, attempting session refresh...');
          setIsRefreshing(true);
          
          const { error: refreshError } = await refreshSession();
          
          if (!refreshError) {
            // Wait before retrying
            await new Promise(resolve => setTimeout(resolve, retryDelay));
            setIsRefreshing(false);
            continue; // Retry the operation
          }
        }
        
        // If not an auth error or max retries reached, break the loop
        break;
      } finally {
        setIsRefreshing(false);
      }
    }
    
    // All retries failed, handle the error
    const wasAuthError = handleAuthError(lastError, context);
    
    if (!wasAuthError) {
      // Re-throw non-auth errors
      throw lastError;
    }
    
    throw new Error('authentication_required');
  }, [user, session, isSessionValid, maxRetries, retryDelay, checkAuthStatus, refreshSession, handleAuthError]);

  return {
    withAuthRetry,
    handleAuthError,
    isAuthError,
    isRefreshing,
    isSessionValid,
  };
};